import {type JSX, useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import type {UnknownAction} from 'redux';
import {saveEventThunk} from '~/CreateFabricInvitation/api/event';
import {useFeatureControls} from '~/CreateFabricInvitation/hooks/useFeatureControls';
import {selectFabricDesign} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {setIsEditingDesign} from '~/CreateFabricInvitation/reducers/preview/reducer';
import {eventIsPremium} from '~/common/fabric/reducers/details/selectors';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {selectUsedPremiumFeature} from '~/common/fabric/reducers/fabricDesign/selectors';
import {initPremiumDesign} from '~/ViewInvitation/reducers/premiumDesign';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';
import {XIcon} from '~library/svg/v2/XIcon';

import './PreviewNav.sass';

export const PreviewNav = (): JSX.Element => {
  const dispatch = useDispatch();
  const {unlockDesignTools} = useFeatureControls();
  // biome-ignore lint/suspicious/noExplicitAny: allow any
  const [savedDesign] = useState<Record<string, any>>(useSelector(selectFabricDesign));
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const isPremium: boolean = useSelector(eventIsPremium);
  const usedPremiumFeature: boolean = useSelector(selectUsedPremiumFeature);

  const applyChanges = useCallback(() => {
    dispatch(saveEventThunk(false) as unknown as UnknownAction);
    if (window.evite_event) {
      dispatch(initPremiumDesign({premiumDesign: window.evite_event.options.design}));
    }
    dispatch(setIsEditingDesign(false));
  }, [dispatch]);

  const handleSave = useCallback(() => {
    if (!isPremium && usedPremiumFeature) {
      unlockDesignTools();
    } else {
      applyChanges();
    }
  }, [isPremium, usedPremiumFeature, unlockDesignTools, applyChanges]);

  const confirmCancel = useCallback(() => {
    dispatch({type: UPDATE_DESIGN_OPTIONS, payload: savedDesign});
    if (window.evite_event) {
      dispatch(initPremiumDesign({premiumDesign: window.evite_event.options.design}));
    }
    dispatch(saveEventThunk(false) as unknown as UnknownAction);
    dispatch(setIsEditingDesign(false));
  }, [dispatch, savedDesign]);

  const handleCancel = useCallback(() => {
    setLeaveModalOpen(true);
  }, []);

  const closeLeaveModal = useCallback(() => setLeaveModalOpen(false), []);

  return (
    <div className="preview-nav">
      <Button variant="unstyled-button" data-qa-id="cancel-edit-design" onClick={handleCancel}>
        <XIcon ratio={0.75} />
      </Button>
      <Typography variant="paragraph2" semibold color="pebble" className="preview-nav__title">
        Edit Invitation design
      </Typography>
      <Button variant="primary" data-qa-id="save-design-changes" onClick={handleSave}>
        Save changes
      </Button>
      <ResponsiveModal
        isOpen={leaveModalOpen}
        onClose={closeLeaveModal}
        primaryText="Exit anyway"
        primaryAction={confirmCancel}
        secondaryText="Keep editing"
        title="Want to exit?"
        body="Your edits have not been saved."
      />
    </div>
  );
};
